import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

type ZoomCapability = { min: number; max: number; step: number };

type ExtendedTrackCapabilities = MediaTrackCapabilities & {
  torch?: boolean;
  zoom?: ZoomCapability;
};

async function safeStopAndClear(scanner: Html5Qrcode) {
  try {
    await scanner.stop();
  } catch {
    // ignore: html5-qrcode throws if already stopped/paused
  }
  try {
    scanner.clear();
  } catch {
    // ignore
  }
}

export function BarcodeScanner(props: {
  open: boolean;
  onDetected: (barcode: string) => void;
  onClose: () => void;
}) {
  const { open, onDetected, onClose } = props;
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomRange, setZoomRange] = useState<ZoomCapability | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const regionId = useMemo(() => `qr-region-${Math.random().toString(16).slice(2)}`, []);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const stoppedRef = useRef(false);
  const refocusTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    stoppedRef.current = false;
    setError(null);
    setStarting(true);
    setHasTorch(false);
    setTorchOn(false);
    setZoomRange(null);
    setZoom(null);

    const scanner = new Html5Qrcode(regionId, {
      verbose: false,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    });
    scannerRef.current = scanner;

    void (async () => {
      try {
        await scanner.start(
          { facingMode: { ideal: "environment" } } as MediaTrackConstraints,
          {
            fps: 24,
            aspectRatio: 1.7777778,
            disableFlip: true,
            qrbox: (viewW, viewH) => {
              const w = Math.floor(Math.min(viewW * 0.85, 720));
              const h = Math.floor(Math.min(viewH * 0.35, w * 0.4));
              return { width: w, height: Math.max(h, 100) };
            },
            videoConstraints: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30, max: 60 },
              advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
            } as MediaTrackConstraints,
          },
          (decodedText) => {
            if (stoppedRef.current) return;
            stoppedRef.current = true;
            onDetected(decodedText);
            void safeStopAndClear(scanner).finally(() => {
              onClose();
            });
          },
          () => {
            // ignore per-frame errors
          },
        );

        // Some browsers ignore advanced constraints in getUserMedia but honor
        // them after start. Re-apply continuous autofocus to kill focus-hunt.
        try {
          await scanner.applyVideoConstraints({
            advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
          } as MediaTrackConstraints);
        } catch {
          // unsupported on this device; harmless
        }

        try {
          const caps = scanner.getRunningTrackCapabilities() as ExtendedTrackCapabilities;
          if (caps.torch) {
            setHasTorch(true);
          }
          if (caps.zoom && typeof caps.zoom.min === "number" && typeof caps.zoom.max === "number") {
            setZoomRange(caps.zoom);
            // Pre-bias zoom slightly so users can read smaller barcodes from
            // farther away without focus-hunt.
            const startZoom =
              caps.zoom.min + (caps.zoom.max - caps.zoom.min) * 0.3;
            const clamped = Math.max(caps.zoom.min, Math.min(caps.zoom.max, startZoom));
            setZoom(clamped);
            try {
              await scanner.applyVideoConstraints({
                advanced: [{ zoom: clamped } as MediaTrackConstraintSet],
              } as MediaTrackConstraints);
            } catch {
              // ignore
            }
          }
        } catch {
          // capabilities not exposed on this browser
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to start camera");
      } finally {
        setStarting(false);
      }
    })();

    return () => {
      stoppedRef.current = true;
      if (refocusTimerRef.current !== null) {
        window.clearTimeout(refocusTimerRef.current);
        refocusTimerRef.current = null;
      }
      const s = scannerRef.current;
      scannerRef.current = null;
      if (!s) return;
      void safeStopAndClear(s);
    };
  }, [onClose, onDetected, open, regionId]);

  async function toggleTorch() {
    const s = scannerRef.current;
    if (!s || !hasTorch) return;
    const next = !torchOn;
    try {
      await s.applyVideoConstraints({
        advanced: [{ torch: next } as MediaTrackConstraintSet],
      } as MediaTrackConstraints);
      setTorchOn(next);
    } catch {
      // device refused; keep state in sync
    }
  }

  async function handleZoomChange(value: number) {
    const s = scannerRef.current;
    if (!s || !zoomRange) return;
    const clamped = Math.max(zoomRange.min, Math.min(zoomRange.max, value));
    setZoom(clamped);
    try {
      await s.applyVideoConstraints({
        advanced: [{ zoom: clamped } as MediaTrackConstraintSet],
      } as MediaTrackConstraints);
    } catch {
      // ignore
    }
  }

  async function pulseFocus() {
    const s = scannerRef.current;
    if (!s) return;
    try {
      await s.applyVideoConstraints({
        advanced: [{ focusMode: "single-shot" } as MediaTrackConstraintSet],
      } as MediaTrackConstraints);
    } catch {
      return;
    }
    if (refocusTimerRef.current !== null) {
      window.clearTimeout(refocusTimerRef.current);
    }
    refocusTimerRef.current = window.setTimeout(() => {
      refocusTimerRef.current = null;
      const cur = scannerRef.current;
      if (!cur) return;
      void cur
        .applyVideoConstraints({
          advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
        } as MediaTrackConstraints)
        .catch(() => {
          // ignore
        });
    }, 600);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-background border shadow-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-medium">Scan barcode</div>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="p-4">
          <div
            className="relative w-full rounded-md overflow-hidden bg-black"
            onClick={() => void pulseFocus()}
            role="button"
            tabIndex={-1}
          >
            <div id={regionId} className="w-full" />
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[2px] bg-red-500/80"
            />
          </div>
          <div className="mt-2 text-xs text-muted-foreground text-center">
            Center the barcode in the box. Tap the preview to refocus.
          </div>

          {(hasTorch || zoomRange) && (
            <div className="mt-3 flex items-center gap-3">
              {hasTorch && (
                <button
                  type="button"
                  onClick={() => void toggleTorch()}
                  className={
                    "px-3 py-1.5 rounded-md border text-sm transition-colors " +
                    (torchOn
                      ? "bg-yellow-400 text-black border-yellow-500"
                      : "bg-background hover:bg-muted")
                  }
                  aria-pressed={torchOn}
                >
                  {torchOn ? "Flash On" : "Flash"}
                </button>
              )}
              {zoomRange && zoom !== null && (
                <label className="flex-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Zoom</span>
                  <input
                    type="range"
                    min={zoomRange.min}
                    max={zoomRange.max}
                    step={zoomRange.step || 0.1}
                    value={zoom}
                    onChange={(e) => void handleZoomChange(Number(e.target.value))}
                    className="flex-1 accent-primary"
                  />
                </label>
              )}
            </div>
          )}

          {starting && <div className="mt-3 text-sm text-muted-foreground">Starting camera…</div>}
          {error && <div className="mt-3 text-sm text-destructive">{error}</div>}
        </div>
      </div>
    </div>
  );
}
